import { EmptyContent } from "@/common";
import { useFullRelease } from "@/common/hooks_useFullRelease";
import { DataSheet, DataSheetWithCtxWrapComp, withCellTooltip } from "@/pages/models/release/Documents/Sheets/DataSheet";
import React from "react";
import { createTextColumn, keyColumn } from "react-datasheet-grid";
import type { Column } from "react-datasheet-grid";
import { useTranslation } from "react-i18next";
import { useDerivedTableDataSource } from "./DerivedTableDataSourceContext";
import { useDerivedTableDebug } from "./DerivedTableDebugContext";
import { formatCellValue } from "./util";

/** Every cell is pre-formatted to a display string by `formatCellValue` before reaching the grid. */
type GridRow = Record<string, string>;

const noop = () => {};
const createRow = (): GridRow => ({});

// Shows the full value in a tooltip on hover, for when a column is too narrow to display it in
// full (eg. long "Description" text truncated by CSS ellipsis).
const readOnlyTextColumn = withCellTooltip(createTextColumn<string>());

/**
 * Renders the selected derived table's rows, shared between the live debugger and the Session
 * Report's stored view - see DerivedTableDataSourceContext for how the two differ. This component
 * only owns presentation; it reads state from whichever data source is currently in scope.
 */
const DerivedTableResultGrid = () => {
  const { t } = useTranslation();
  const release = useFullRelease();
  const { selectedTableId } = useDerivedTableDebug();
  const { state, messages } = useDerivedTableDataSource();

  const derivedTables = release?.derivedTables || [];
  const tableDefinition = derivedTables.find((table) => table.id === selectedTableId);

  const gridColumns: Column<GridRow>[] = React.useMemo(
    () =>
      (tableDefinition?.cols || []).map((column) => ({
        ...keyColumn<GridRow, string>(column.n, readOnlyTextColumn),
        title: column.n,
        disabled: true,
      })),
    [tableDefinition],
  );

  const rows: GridRow[] = React.useMemo(() => {
    if (state.status !== "ready") return [];
    return state.rows.map((row) => {
      const formatted: GridRow = {};
      for (const column of tableDefinition?.cols || []) {
        formatted[column.n] = formatCellValue(row[column.n] as any);
      }
      return formatted;
    });
  }, [state, tableDefinition]);

  if (derivedTables.length === 0) {
    return (
      <EmptyContent
        width="200px"
        messages={[
          { text: t("debugger.derived_table_none_in_release_title"), variant: "h5" },
          { text: t("debugger.derived_table_none_in_release_description") },
        ]}
      />
    );
  }

  if (!tableDefinition) {
    return (
      <EmptyContent
        width="200px"
        messages={[{ text: t("debugger.derived_table_no_table_title"), variant: "h5" }]}
      />
    );
  }

  if (state.status === "no-options") {
    return (
      <EmptyContent
        width="200px"
        messages={[
          { text: t(messages.noOptionsTitleKey), variant: "h5" },
          { text: t(messages.noOptionsDescriptionKey) },
        ]}
      />
    );
  }

  if (state.status === "error") {
    return (
      <EmptyContent
        width="200px"
        messages={[
          { text: t(messages.errorTitleKey), variant: "h5" },
          { text: t(messages.errorDescriptionKey) },
        ]}
      />
    );
  }

  if (state.status === "pending") {
    return (
      <EmptyContent
        width="200px"
        messages={[
          { text: t(messages.pendingTitleKey), variant: "h5" },
          { text: t(messages.pendingDescriptionKey) },
        ]}
      />
    );
  }

  return (
    <div className="h-full flex flex-col overflow-auto p-4">
      <DataSheet
        staticColumns
        lockRows
        columns={gridColumns}
        data={rows}
        createRow={createRow}
        onChange={noop}
        CtxWrapComp={DataSheetWithCtxWrapComp}
      />
    </div>
  );
};

export default DerivedTableResultGrid;
